import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import tinycolor from 'tinycolor2';

import { joinClassNames, STORY_ACCENT_COLOR } from '../../../util/util';
import * as QPlayerSVGElement from '../../QPlayerSVGElement';
import ControlButton from '../ControlButton';
import { usePlayer } from '../../../contexts/PlayerContext';
import { useConfiguration } from '../../../contexts/ConfigurationContext';

import './CenterControls.scss';
import { isReelsFeedEnabled, isStoryFeedPreset } from '../../StoryFeed/story-feed-helper';

interface CenterControlsProps {
    settings?: any;
    playerHover?: boolean;
    isMobile?: boolean;
    className?: string;
}

export const StoryCenterControls = ({ settings, className }: CenterControlsProps) => {
    const { t } = useTranslation();
    const { isPlaying } = usePlayer();
    const { currentMediaId, playlistId, configParams } = useConfiguration();

    const [showPlayButton, setShowPlayButton] = React.useState(true);
    // Kept apart from the hover flag rather than folded into it: the two can be true at once, and
    // sharing one flag means tabbing away fades the disc out from under a pointer still resting on
    // it. WCAG 2.4.7 - a control that takes focus has to be visible while it has it, and this one
    // was reachable by Tab while sitting at opacity 0.
    const [isPlayButtonFocused, setIsPlayButtonFocused] = React.useState(false);

    // Every Story playlist mounts one player per clip - player-<mediaId>-<key> - so a playlist id
    // here names no widget and every call through it reaches nothing. This used to test for the
    // reels feed alone, which left the centre button dead on a Story preview link and a normal
    // Story embed: clicking the disc did nothing while clicking the picture around it paused.
    const currentPlayerId = `player-${isStoryFeedPreset(configParams) ? currentMediaId : (playlistId || currentMediaId)}-${configParams.containerKey}`;
    // The Story default, from the design. A preset that sets primaryColor still wins, so this is
    // Børsen's pink out of the box and anyone else's brand where they have chosen one.
    const color = settings?.playerStyling?.primaryColor ?? STORY_ACCENT_COLOR;
    const opacity = Number(settings?.playerStyling?.primaryOpacity ?? 100);
    const rgbaWithOpa = { ...tinycolor(color).toRgb(), a: opacity / 100 };
    const hoverStyle = { backgroundColor: tinycolor(rgbaWithOpa).toRgbString() };
    const solidColor = settings?.controls?.solidBackgroundColor ?? '#000000';
    const solidColorStyle = settings?.controls?.showSolidBackgroundControl
        ? {
            backgroundColor: tinycolor({
                ...tinycolor(solidColor).toRgb(),
                a: Number(settings?.controls?.solidBackgroundControlOpacity ?? 100) / 100,
            }).toRgbString(),
        }
        : {};

    // One timer, cleared before it is replaced. Leaving and re-entering inside the second used to
    // stack them, so an old one could hide the button while the pointer was still on it.
    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const clearHideTimer = useCallback(() => {
        if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
    }, []);
    useEffect(() => clearHideTimer, [clearHideTimer]);

    const handleButtonPlay = useCallback(() => {
        (window as any).GoBrain.widgets(currentPlayerId)?.play?.();
    }, [currentPlayerId]);

    const handleButtonPause = useCallback(() => {
        (window as any).GoBrain.widgets(currentPlayerId)?.pause?.();
    }, [currentPlayerId]);

    // Keyed on isPlaying, not on mount. Nothing is playing when this first renders - the clip is
    // still being handed to GoBrain - so an empty dependency list asked the question before there
    // was an answer, never armed the timer, and left the button up for the whole clip. Re-armed on
    // every resume, and cleared on the way out so a pause does not get a stale hide a second later.
    //
    // Feeds only. A Story preset on an ordinary embed keeps the behaviour it shipped with, whatever
    // that is worth - this is a fix for the reel, not an excuse to change every Story player.
    const isReelsFeed = isReelsFeedEnabled(configParams);
    useEffect(() => {
        if (!isReelsFeed || !isPlaying) {
            return undefined;
        }
        const timer = setTimeout(() => setShowPlayButton(false), 2000);
        return () => clearTimeout(timer);
    }, [isReelsFeed, isPlaying]);

    // The behaviour this replaces, kept for every other Story player: armed once, on mount.
    useEffect(() => {
        if (isReelsFeed) {
            return undefined;
        }
        if (isPlaying) {
            const timer = setTimeout(() => setShowPlayButton(false), 2000);
            return () => clearTimeout(timer);
        }
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={joinClassNames('controlsOverlayCenterControls--story', className)}>
            <div
                className='controlsOverlayCenterControls__play-btn-container'
                // Pointer events, read by type. A touch has no hover to leave: the browser
                // synthesises an enter on tap and often never sends the matching leave, so hanging
                // the hide off mouseleave kept the button up for the rest of the clip on a phone.
                // A mouse keeps hover; a finger gets the same second's grace counted from the tap
                // instead. Either way a paused clip still shows it, through !isPlaying.
                onPointerEnter={(event) => {
                    if (event.pointerType !== 'mouse') {
                        return;
                    }
                    clearHideTimer();
                    setShowPlayButton(true);
                }}
                onPointerLeave={(event) => {
                    if (event.pointerType !== 'mouse') {
                        return;
                    }
                    clearHideTimer();
                    hideTimerRef.current = setTimeout(() => setShowPlayButton(false), 1000);
                }}
                onPointerUp={(event) => {
                    if (event.pointerType === 'mouse') {
                        return;
                    }
                    clearHideTimer();
                    hideTimerRef.current = setTimeout(() => setShowPlayButton(false), 1000);
                }}
                // React's onFocus/onBlur are focusin/focusout, so they fire for the button inside.
                //
                // :focus-visible, not plain focus. Clicking the button focuses it, and a focus that
                // pins the control visible meant one click left it up for the rest of the clip -
                // the pointer could leave, the timer could fire, and this flag still said "keep it".
                // The browser already distinguishes the two: it only matches :focus-visible when
                // focus arrived by keyboard or assistive tech, which is the case that genuinely
                // needs the button to stay put.
                onFocus={(event) =>
                    setIsPlayButtonFocused(!!(event.target as HTMLElement)?.matches?.(':focus-visible'))
                }
                onBlur={() => setIsPlayButtonFocused(false)}
            >
                <ControlButton
                    tabIndex={0}
                    className={joinClassNames(
                        'controlsOverlayCenterControls__play-btn',
                        isPlaying ? 'controlsOverlayCenterControls__play-btn--playing' : ''
                    )}
                    onClick={isPlaying ? handleButtonPause : handleButtonPlay}
                    // Both marks are always mounted and cross-faded by the stylesheet. Swapping the
                    // node instead made the icon jump; the reference has one dissolve into the other.
                    icon={
                        <span className="controlsOverlayCenterControls__play-icons">
                            <span className="controlsOverlayCenterControls__play-icon controlsOverlayCenterControls__play-icon--play">
                                <QPlayerSVGElement.PlayStory />
                            </span>
                            <span className="controlsOverlayCenterControls__play-icon controlsOverlayCenterControls__play-icon--pause">
                                <QPlayerSVGElement.Pause />
                            </span>
                        </span>
                    }
                    hoverStyle={hoverStyle}
                    style={{
                        ...solidColorStyle,
                        // A filled disc in the accent, not a glyph on the picture.
                        backgroundColor: tinycolor(rgbaWithOpa).toRgbString(),
                        opacity: showPlayButton || isPlayButtonFocused || !isPlaying ? '1' : '0',
                    }}
                    aria-label={isPlaying ? t('PAUSE') : t('PLAY')}
                />
            </div>
        </div>
    );
}